import React from 'react';
import { DURATION, MIN_EMPTY_ANGLE, MIN_STROKE_ANGLE, BEZIER, propTypes } from './shared';
import { useA11yInfo } from '../A11yInfoProvider';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SVG_RADIUS = 20;
const SVG_CIRCUMFERENCE = SVG_RADIUS * 2 * Math.PI;
const MIN_SVG_LENGTH = MIN_STROKE_ANGLE / 360 * SVG_CIRCUMFERENCE;
const MAX_SVG_LENGTH = (1 - MIN_EMPTY_ANGLE / 360) * SVG_CIRCUMFERENCE;
const animationProps = {
  begin: '0s',
  dur: `${DURATION}ms`,
  fill: 'freeze',
  repeatCount: 'indefinite'
};
const bezierProps = {
  calcMode: 'spline',
  keyTimes: '0; 0.5; 1',
  keySplines: `${BEZIER.join(', ')} ; ${BEZIER.join(', ')}`
};
// We're using svg rather than css here to define the animation to avoid needing to introduce css injection mechanism
// It's possible to replicate this functionality with RNW animations, but it snags on chrome at least, see https://github.com/telus/universal-design-system/pull/477 for details.
const Spinner = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    size,
    color,
    thickness,
    label,
    isStatic = false
  } = _ref;
  const {
    reduceMotionEnabled
  } = useA11yInfo();
  const reduceMotion = reduceMotionEnabled || isStatic;
  return /*#__PURE__*/_jsx("svg", {
    ref: ref,
    width: `${size}px`,
    height: `${size}px`,
    viewBox: "0 0 48 48",
    "aria-valuetext": label,
    role: "progressbar",
    "aria-busy": true,
    children: /*#__PURE__*/_jsxs("g", {
      children: [reduceMotion ? null : /*#__PURE__*/_jsx("animateTransform", {
        attributeName: "transform",
        type: "rotate",
        values: `-180 24 24;${360 + MIN_STROKE_ANGLE - 180} 24 24`,
        ...animationProps
      }), /*#__PURE__*/_jsx("circle", {
        fill: "none",
        stroke: color,
        strokeWidth: thickness * 48 / size,
        strokeLinecap: "round",
        cx: "24",
        cy: "24",
        r: "20",
        strokeDasharray: reduceMotion ? MAX_SVG_LENGTH : [MIN_SVG_LENGTH, SVG_CIRCUMFERENCE],
        strokeDashoffset: 0,
        vectorEffect: "non-scaling-stroke",
        children: reduceMotion ? null : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("animate", {
            attributeName: "stroke-dashoffset",
            values: `0;-10;${MIN_SVG_LENGTH - SVG_CIRCUMFERENCE}`,
            ...animationProps,
            ...bezierProps
          }), /*#__PURE__*/_jsx("animate", {
            attributeName: "stroke-dasharray",
            values: `${MIN_SVG_LENGTH}, 200;${MAX_SVG_LENGTH}, 200;${MIN_SVG_LENGTH}, 200`,
            ...animationProps,
            ...bezierProps
          })]
        })
      })]
    })
  });
});
Spinner.displayName = 'Spinner';
Spinner.propTypes = propTypes;
export default Spinner;